import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { PageTitle, PageLink } from '../../../rbts/layout/core';
import { LoadingGrid, formatPrice } from '../../../rbts/helpers';
import { styleSelect, themeSelect } from '../../../rbts/assets/ts/_utils';
import { useObtenerDiasPorEvento } from './cortesias/hooks/CortesiasHook';

const API_URL = process.env.REACT_APP_API;


const formatFecha = (fechaStr: string) => {
  if (!fechaStr) return '—';
  const d = new Date(fechaStr);
  return d.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function ReporteRetiros() {
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [folioFiltro, setFolioFiltro] = useState<string>('');
  const [list, setList] = useState<any[]>([]);
  const [retirosDetalle, setRetirosDetalle] = useState<Record<string, any[]>>({});
  const [expandedCaja, setExpandedCaja] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [montoLimite, setMontoLimite] = useState<number>(0);

  const { list: listadosDeDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);

  useEffect(() => {
    axios.get(`${API_URL}retiro/monto-limite`)
      .then(res => setMontoLimite(parseFloat(res.data.doc.data.monto_limite) || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const ids = diasSeleccionados.map((d: any) => d.value as number);
    if (ids.length === 0) {
      setList([]);
      setRetirosDetalle({});
      setExpandedCaja(null);
      return;
    }
    setLoading(true);
    setExpandedCaja(null);

    Promise.all(
      ids.map(diaId =>
        Promise.all([
          axios.get(`${API_URL}retiro/reporte/${diaId}`),
          axios.get(`${API_URL}retiro/dia/${diaId}`),
        ])
      )
    )
      .then(results => {
        const combinedList: any[] = [];
        const agrupado: Record<string, any[]> = {};

        results.forEach(([resReporte, resDetalle]) => {
          (resReporte.data.doc.data || []).forEach((row: any) => {
            const existing = combinedList.find(r => r.caja === row.caja);
            if (existing) {
              existing.venta_acumulada = (existing.venta_acumulada || 0) + (row.venta_acumulada || 0);
              existing.monto_retirado  = (existing.monto_retirado  || 0) + (row.monto_retirado  || 0);
              existing.total_real      = (existing.total_real      || 0) + (row.total_real      || 0);
            } else {
              combinedList.push({ ...row });
            }
          });

          (resDetalle.data.doc.data || []).forEach((r: any) => {
            const caja = r.punto_venta?.nombre ?? r.punto_venta ?? 'Sin nombre';
            if (!agrupado[caja]) agrupado[caja] = [];
            agrupado[caja].push(r);
          });
        });

        setList(combinedList);
        setRetirosDetalle(agrupado);
      })
      .catch(() => {
        toast.error('Error al obtener el reporte de retiros');
        setList([]);
        setRetirosDetalle({});
      })
      .finally(() => setLoading(false));
  }, [diasSeleccionados]);

  const listFiltrado = folioFiltro.trim()
    ? list.filter(row => {
        const detalle = retirosDetalle[row.caja] ?? [];
        return detalle.some((r: any) =>
          String(r.folio ?? '').toLowerCase().includes(folioFiltro.trim().toLowerCase())
        );
      })
    : list;

  const widgetsBreadCrumbs: Array<PageLink> = [
    { title: 'Reportes', path: '/reportes/retiros', isSeparator: false, isActive: false },
    { title: '', path: '', isSeparator: true, isActive: false },
  ];

  const renderAlerta = (row: any) => {
    const total = row.total_real;
    if (montoLimite > 0 && total > montoLimite) {
      return (
        <span className="badge badge-light-danger fw-bold px-3 py-2 d-flex align-items-center gap-1">
          <i className="bi bi-circle-fill text-danger" style={{ fontSize: '10px' }} />
          Requiere retiro
        </span>
      );
    }
    if (montoLimite > 0 && total > montoLimite * 0.75) {
      return (
        <span className="badge badge-light-warning fw-bold px-3 py-2 d-flex align-items-center gap-1">
          <i className="bi bi-circle-fill text-warning" style={{ fontSize: '10px' }} />
          Cerca del límite
        </span>
      );
    }
    return (
      <span className="badge badge-light-success fw-bold px-3 py-2 d-flex align-items-center gap-1">
        <i className="bi bi-check-square-fill text-success" style={{ fontSize: '12px' }} />
        OK
      </span>
    );
  };

  const handleDownloadExcel = async () => {
    setLoadingDownload(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Reporte de Retiros');

      ws.columns = [
        { header: 'Caja',             key: 'caja',             width: 20 },
        { header: 'Monto Inicial',    key: 'inicio',           width: 16 },
        { header: 'Venta Acumulada',  key: 'venta_acumulada',  width: 16 },
        { header: 'Monto Retirado',   key: 'monto_retirado',   width: 16 },
        { header: 'Monto Actual',     key: 'total_real',       width: 16 },
        { header: 'Folio Retiro',     key: 'folio',            width: 18 },
        { header: 'Usuario',          key: 'usuario',          width: 22 },
        { header: 'Monto Retiro',     key: 'total_retiro',     width: 16 },
        { header: 'Fecha / Hora',     key: 'fecha_retiro',     width: 22 },
      ];

      ws.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
      ws.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      list.forEach(row => {
        const detalle = retirosDetalle[row.caja] ?? [];
        if (detalle.length === 0) {
          ws.addRow({
            caja:            row.caja,
            inicio:          Number(row.inicio || 0),
            venta_acumulada: Number(row.venta_acumulada || 0),
            monto_retirado:  Number(row.monto_retirado || 0),
            total_real:      Number(row.total_real || 0),
            folio:           '—',
            usuario:         '—',
            total_retiro:    0,
            fecha_retiro:    '—',
          });
        } else {
          detalle.forEach((r: any, idx: number) => {
            ws.addRow({
              caja:            idx === 0 ? row.caja : '',
              inicio:          idx === 0 ? Number(row.inicio || 0) : '',
              venta_acumulada: idx === 0 ? Number(row.venta_acumulada || 0) : '',
              monto_retirado:  idx === 0 ? Number(row.monto_retirado || 0) : '',
              total_real:      idx === 0 ? Number(row.total_real || 0) : '',
              folio:           r.folio ?? '—',
              usuario:         r.usuario ?? '—',
              total_retiro:    Number(r.total_retiro || 0),
              fecha_retiro:    formatFecha(r.fecha),
            });
          });
        }
        ws.addRow({});
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte_retiros.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al exportar Excel:', err);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <>
      <PageTitle breadcrumbs={widgetsBreadCrumbs}>Reporte de Retiros</PageTitle>

      {/* ── Filtros ─────────────────────────────────────────────────── */}
      <div className="card mb-5">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bolder fs-3 mb-1">Filtros de Reporte</span>
            <span className="text-muted mt-1 fw-bold fs-7">
              Seleccione el día administrativo para visualizar el reporte de retiros
            </span>
          </h3>
        </div>
        <div className="card-body py-3">
          <div className="row">
            <div className="col-lg-5">
              <label className="fw-bold fs-6 mb-2">Días Administrativos</label>
              <Select
                value={diasSeleccionados}
                isLoading={cargandoDias}
                options={listadosDeDias}
                styles={styleSelect}
                theme={themeSelect}
                classNamePrefix="tc-select"
                placeholder="Seleccione uno o varios días..."
                onChange={(opts: any) => setDiasSeleccionados(opts ?? [])}
                isMulti
                closeMenuOnSelect={false}
                isClearable
                noOptionsMessage={() => 'Sin opciones'}
              />
            </div>
            <div className="col-lg-3">
              <label className="fw-bold fs-6 mb-2">Folio</label>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por folio retiro..."
                value={folioFiltro}
                onChange={(e) => setFolioFiltro(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Tabla ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header border-0 pt-5">
          <div className="card-title">
            <h3 className="card-label fw-bolder text-dark">Reporte de Retiros</h3>
          </div>
          <div className="card-toolbar">
            {list.length > 0 && (
              <button
                className="btn btn-sm btn-light-success"
                onClick={handleDownloadExcel}
                disabled={loadingDownload}
              >
                {loadingDownload ? (
                  <span className="indicator-progress" style={{ display: 'block' }}>
                    Descargando...
                    <span className="spinner-border spinner-border-sm align-middle ms-2" />
                  </span>
                ) : (
                  'Descargar Excel'
                )}
              </button>
            )}
          </div>
        </div>
        <div className="card-body pt-3">
          {diasSeleccionados.length === 0 ? (
            <div className="text-center text-muted py-10 fs-6">
              Seleccione al menos un día administrativo para ver el reporte.
            </div>
          ) : loading ? (
            <LoadingGrid text="Cargando reporte..." />
          ) : listFiltrado.length === 0 ? (
            <div className="text-center text-muted py-10 fs-6">
              No se encontraron registros para los días seleccionados.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-rounded border gy-4 gs-7">
                <thead>
                  <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                    <th>Caja</th>
                    <th className="text-end">Monto Inicial</th>
                    <th className="text-end">Venta Acumulada</th>
                    <th className="text-end">Monto Retirado</th>
                    <th className="text-end">Monto Actual</th>
                    <th className="text-center">Alerta de Retiro</th>
                    <th>Fecha de Ingreso</th>
                  </tr>
                </thead>
                <tbody>
                  {listFiltrado.map((row: any, idx: number) => {
                    const isExpanded = expandedCaja === row.caja;
                    const detalleAll = retirosDetalle[row.caja] ?? [];
                    const detalle = folioFiltro.trim()
                      ? detalleAll.filter((r: any) =>
                          String(r.folio ?? '').toLowerCase().includes(folioFiltro.trim().toLowerCase())
                        )
                      : detalleAll;
                    return (
                      <React.Fragment key={`${row.caja}-${idx}`}>
                        <tr className="align-middle">
                          <td className="fs-7">
                            <button
                              className="btn btn-link p-0 fw-bold text-start d-flex align-items-center gap-1"
                              style={{ color: 'var(--tc-primary)', textDecoration: 'none' }}
                              onClick={() => setExpandedCaja(isExpanded ? null : row.caja)}
                              title={isExpanded ? 'Ocultar detalle' : 'Ver detalle de retiros'}
                            >
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} fs-7`} />
                              {row.caja}
                            </button>
                          </td>
                          <td className="text-end fs-7">
                            {formatPrice(row.inicio, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-end fs-7">
                            {formatPrice(row.venta_acumulada, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-end fs-7 fw-semibold tc-amount-negative">
                            {formatPrice(row.monto_retirado, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-end fs-7 fw-bold tc-amount-positive">
                            {formatPrice(row.total_real, 'es-MX', 'MXN', 2, 2)}
                          </td>
                          <td className="text-center fs-7">{renderAlerta(row)}</td>
                          <td className="fs-7">
                            {new Date(row.fecha).toLocaleDateString('es-MX')}
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td
                              colSpan={7}
                              style={{ padding: '4px 16px 12px 40px', backgroundColor: 'var(--tc-bg-raised)' }}
                            >
                              {detalle.length === 0 ? (
                                <div className="text-muted fs-7 py-3 fst-italic">
                                  Sin retiros registrados para esta caja
                                </div>
                              ) : (
                                <table className="table table-sm mb-0 border" style={{ borderRadius: '6px', overflow: 'hidden' }}>
                                  <thead>
                                    <tr className="tc-report-detail">
                                      <th className="fs-7 fw-bolder py-2 ps-3" style={{ color: 'var(--tc-primary)' }}>Folio</th>
                                      <th className="fs-7 fw-bolder py-2" style={{ color: 'var(--tc-primary)' }}>Usuario</th>
                                      <th className="fs-7 fw-bolder py-2 text-end" style={{ color: 'var(--tc-primary)' }}>Monto Retirado</th>
                                      <th className="fs-7 fw-bolder py-2" style={{ color: 'var(--tc-primary)' }}>Fecha / Hora</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detalle.map((r: any, i: number) => (
                                      <tr
                                        key={`det-${r.id}-${i}`}
                                        className="align-middle"
                                        style={{ borderBottom: '1px dashed var(--tc-border)' }}
                                      >
                                        <td className="fs-7 py-2 ps-3 fw-semibold" style={{ color: 'var(--tc-primary)' }}>
                                          {r.folio ?? '—'}
                                        </td>
                                        <td className="fs-7 py-2">{r.usuario ?? '—'}</td>
                                        <td className="fs-7 py-2 text-end fw-bold tc-amount-negative">
                                          {formatPrice(r.total_retiro ?? 0, 'es-MX', 'MXN', 2, 2)}
                                        </td>
                                        <td className="fs-7 py-2">{formatFecha(r.fecha)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
